// main.ts
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { environment } from '@environments/environment';
import { provideRouter, Router } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideIonicAngular } from '@ionic/angular';
import { AppComponent } from '@app/app.component';
import { APP_ROUTES } from '@app/app.routes';
import { AppInitializerProvider } from '@app/core/init/app-initializer.provider';
import { provideLegacyIonicElements } from '@app/core/init/ionic-lazy-elements';
import { maintenanceInterceptor } from '@app/core/interceptors/maintenance.interceptor';
import { forceUpdateInterceptor } from '@app/core/interceptors/force-update.interceptor';
import { GlobalErrorHandler } from '@app/core/error-handler/global-error-handler';
import { FirebaseCrashlyticsService } from '@app/shared/services/firebase-crashlytics.service';
import { ForceUpdateStateService } from '@app/shared/services/force-update-state.service';
import { Capacitor } from '@capacitor/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(APP_ROUTES),
    provideHttpClient(withXhr(), withInterceptors([forceUpdateInterceptor, maintenanceInterceptor])),

    provideIonicAngular({
      backButtonText: '', // iOSのバックボタンのテキストを空にする
    }),
    provideLegacyIonicElements(),

    // ✅ ngx-translate 設定（新API: provideTranslateHttpLoader でローダーを提供）
    importProvidersFrom(TranslateModule.forRoot()),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
      // 以前 HttpBackend を使ってインターセプターをバイパスしていた要件に対応
      useHttpBackend: true,
    }),

    // デフォルト言語設定（非推奨オプション defaultLanguage の代替実装）
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      translate.setFallbackLang('ja');
      translate.use('ja');
    }),

    provideAppInitializer(() => {
      if (environment.production || !environment.forceUpdateDevTest) {
        return;
      }
      const state = inject(ForceUpdateStateService);
      const router = inject(Router);
      state.set({
        active: true,
        message: '開発用: 強制アップデート画面のテストです。',
      });
      setTimeout(() => {
        void router.navigateByUrl('/force-update', { replaceUrl: true });
      }, 200);
    }),

    AppInitializerProvider,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideAppInitializer(async () => {
      const crashlytics = inject(FirebaseCrashlyticsService);
      const isNative = Capacitor.getPlatform() !== 'web';
      await crashlytics.setEnabled(isNative);
      if (isNative) {
        crashlytics.attachGlobalHandlers();
        await crashlytics.log(
          'Crashlytics initialized (app initializer)',
          'info'
        );
      }
    }),
  ],
}).catch((err) => console.error(err));
